/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================
*/

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import AppLoader from "components/AppLoader";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import { useEffect, useState } from "react";
import fetchWithAuth from "layouts/tables/data/api";
import endpointRolesTableData from "layouts/tables/data/endpointRolesTableData";
import appSettings from "appsettings.json";

function EndpointRoles() {
  const [endpointRoles, setEndpointRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deletingEndpointRoleId, setDeletingEndpointRoleId] = useState(null);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [selectedEndpointRole, setSelectedEndpointRole] = useState(null);

  const normalizeArray = (value) => {
    if (Array.isArray(value)) return value;
    if (value && Array.isArray(value.$values)) return value.$values;
    return [];
  };

  const fetchEndpointRoles = async () => {
    setLoading(true);
    setError(null);

    const url = `${appSettings.baseUrl}${appSettings.endpoints.getAllEndpointRoles}`;
    try {
      const response = await fetchWithAuth(url, {
        method: "GET",
        cache: "no-store",
      });

      if (!response.ok) {
        throw new Error("An error occurred while fetching endpoint roles.");
      }

      const data = await response.json();
      setEndpointRoles(normalizeArray(data));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEndpointRoles();
  }, []);

  const handleOpenDeleteConfirm = (endpointRole) => {
    if (!endpointRole?.endpointRoleId) {
      setError("Unable to remove endpoint role: assignment id is missing.");
      return;
    }

    setSelectedEndpointRole(endpointRole);
    setOpenDeleteConfirm(true);
  };

  const handleCloseDeleteConfirm = () => {
    if (deletingEndpointRoleId !== null) return;
    setOpenDeleteConfirm(false);
    setSelectedEndpointRole(null);
  };

  const handleConfirmDelete = async () => {
    const endpointRoleId = selectedEndpointRole?.endpointRoleId;
    if (!endpointRoleId) {
      setError("Unable to remove endpoint role: assignment id is missing.");
      return;
    }

    setDeletingEndpointRoleId(endpointRoleId);
    setError(null);

    const formBody = new URLSearchParams();
    formBody.append("id", String(endpointRoleId));
    const url = `${appSettings.baseUrl}${appSettings.endpoints.deleteEndpointRole}`;

    try {
      const response = await fetchWithAuth(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: formBody.toString(),
      });

      if (!response.ok) {
        let errorMessage = "An error occurred while removing endpoint role.";
        try {
          const errorData = await response.json();
          errorMessage = errorData?.title || errorData?.message || errorData?.error || errorMessage;
        } catch (e) {
          // Keep default error message when response body is not JSON.
        }
        throw new Error(errorMessage);
      }

      setEndpointRoles((prev) =>
        prev.filter((item) => {
          const id = item?.endpointRoleId ?? item?.EndpointRoleId ?? item?.id ?? item?.Id ?? null;
          return String(id) !== String(endpointRoleId);
        })
      );

      setOpenDeleteConfirm(false);
      setSelectedEndpointRole(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setDeletingEndpointRoleId(null);
    }
  };

  const { columns, rows } = endpointRolesTableData(
    endpointRoles,
    handleOpenDeleteConfirm,
    deletingEndpointRoleId
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="success"
                borderRadius="lg"
                coloredShadow="success"
              >
                <MDTypography variant="h6" color="white">
                  Endpoint Roles
                </MDTypography>
              </MDBox>
              <MDBox pt={3}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" alignItems="center" py={10}>
                    <AppLoader />
                  </MDBox>
                ) : error ? (
                  <MDBox p={3}>
                    <MDTypography variant="button" color="error">
                      Error: {error}
                    </MDTypography>
                  </MDBox>
                ) : (
                  <DataTable
                    table={{ columns, rows }}
                    canSearch
                    isSorted={true}
                    entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20] }}
                    showTotalEntries={true}
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      <Dialog open={openDeleteConfirm} onClose={handleCloseDeleteConfirm} fullWidth maxWidth="xs">
        <DialogTitle>Remove Endpoint Role</DialogTitle>
        <DialogContent>
          <MDTypography variant="button" color="text">
            Are you sure you want to remove this endpoint role?
          </MDTypography>
          {selectedEndpointRole && (
            <MDBox mt={1}>
              <MDTypography variant="caption" color="text" fontWeight="medium">
                {selectedEndpointRole.endpointNameText} - {selectedEndpointRole.roleNameText}
              </MDTypography>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton
            onClick={handleCloseDeleteConfirm}
            color="secondary"
            disabled={deletingEndpointRoleId !== null}
          >
            Cancel
          </MDButton>
          <MDButton
            onClick={handleConfirmDelete}
            variant="gradient"
            color="error"
            disabled={deletingEndpointRoleId !== null}
          >
            Remove
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default EndpointRoles;
